import type { FantasyPlayer, MatchFantasyPlayer, PlayerPosition } from '../../types'

export type Card_ = MatchFantasyPlayer

export type PitchRow = { position: PlayerPosition; cols: number[] }
export type PitchSlot = {
  key: string
  position: PlayerPosition
  row: number
  col: number
}

// The pitch grid: rows run from the forwards down to the goalkeeper and every
// row is laid out over five columns so the shape of the formation is visible.
export const PITCH_LAYOUTS: Record<string, PitchRow[]> = {
  '4-4-2': [
    { position: 'fwd', cols: [1, 3] },
    { position: 'mid', cols: [0, 1, 3, 4] },
    { position: 'def', cols: [0, 1, 3, 4] },
    { position: 'gk', cols: [2] },
  ],
  '4-3-3': [
    { position: 'fwd', cols: [0, 2, 4] },
    { position: 'mid', cols: [0, 2, 4] },
    { position: 'def', cols: [0, 1, 3, 4] },
    { position: 'gk', cols: [2] },
  ],
  '4-5-1': [
    { position: 'fwd', cols: [2] },
    { position: 'mid', cols: [0, 1, 2, 3, 4] },
    { position: 'def', cols: [0, 1, 3, 4] },
    { position: 'gk', cols: [2] },
  ],
  '3-5-2': [
    { position: 'fwd', cols: [1, 3] },
    { position: 'mid', cols: [0, 1, 2, 3, 4] },
    { position: 'def', cols: [1, 2, 3] },
    { position: 'gk', cols: [2] },
  ],
  '5-3-2': [
    { position: 'fwd', cols: [1, 3] },
    { position: 'mid', cols: [0, 2, 4] },
    { position: 'def', cols: [0, 1, 2, 3, 4] },
    { position: 'gk', cols: [2] },
  ],
  '4-1-4-1': [
    { position: 'fwd', cols: [2] },
    { position: 'mid', cols: [0, 1, 3, 4] },
    { position: 'mid', cols: [2] },
    { position: 'def', cols: [0, 1, 3, 4] },
    { position: 'gk', cols: [2] },
  ],
}

/** i18n keys under `dreamTeam.positions`. Display with getPositionLabel(pos, t). */
export const POSITION_LABEL_KEYS: Record<PlayerPosition, string> = {
  gk: 'dreamTeam.positions.gk',
  def: 'dreamTeam.positions.def',
  mid: 'dreamTeam.positions.mid',
  fwd: 'dreamTeam.positions.fwd',
}

/** Same keys as POSITION_LABEL_KEYS, kept so existing imports still resolve. */
export const POSITION_LABELS: Record<PlayerPosition, string> = POSITION_LABEL_KEYS

type PositionTranslate = (
  key: string,
  vars?: Record<string, string | number | boolean | null | undefined>,
) => string

export function getPositionLabel(pos: PlayerPosition | string, t: PositionTranslate): string {
  const key = POSITION_LABEL_KEYS[pos as PlayerPosition]
  return key ? t(key) : pos
}

export const POSITION_ICONS: Record<PlayerPosition, string> = {
  gk: '🥅',
  def: '🛡️',
  mid: '⚽',
  fwd: '🎯',
}

export const POSITION_ORDER: PlayerPosition[] = ['gk', 'def', 'mid', 'fwd']

export function buildSlots(formation: string): PitchSlot[] {
  const rows = PITCH_LAYOUTS[formation] || PITCH_LAYOUTS['4-4-2']
  const counters: Partial<Record<PlayerPosition, number>> = {}
  const slots: PitchSlot[] = []
  rows.forEach((row, rowIndex) => {
    row.cols.forEach((col) => {
      const index = counters[row.position] ?? 0
      counters[row.position] = index + 1
      slots.push({ key: `${row.position}-${index}`, position: row.position, row: rowIndex, col })
    })
  })
  return slots
}

export function toCard(player: FantasyPlayer): Card_ {
  return {
    id: player.id,
    name: player.name,
    position: player.position,
    team: player.team,
    photo_url: player.photo_url,
    group_ids: [],
    group_titles: [],
  }
}

// Distributes cards over the slots of a formation, position by position, keeping the order.
export function assignByPosition(slots: PitchSlot[], cards: Card_[]): Record<string, Card_> {
  const byPosition: Record<string, Card_[]> = {}
  cards.forEach((option) => {
    byPosition[option.position] = byPosition[option.position] || []
    byPosition[option.position].push(option)
  })

  const lineup: Record<string, Card_> = {}
  slots.forEach((slot) => {
    const bucket = byPosition[slot.position]
    if (bucket && bucket.length > 0) {
      lineup[slot.key] = bucket.shift() as Card_
    }
  })
  return lineup
}